import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { ContextMenuItem, ContextMenuState, Position } from '../types';

interface ContextMenuContextType {
  menuState: ContextMenuState;
  showContextMenu: (position: Position, items: ContextMenuItem[], targetType?: 'desktop' | 'icon' | 'taskbar', targetId?: string) => void;
  hideContextMenu: () => void;
}

const initialState: ContextMenuState = {
  isOpen: false,
  position: { x: 0, y: 0 },
  items: [],
  targetType: null,
  targetId: undefined,
};

const ContextMenuContext = createContext<ContextMenuContextType | null>(null);

interface ContextMenuProviderProps {
  children: ReactNode;
}

export const ContextMenuProvider: React.FC<ContextMenuProviderProps> = ({ children }) => {
  const [menuState, setMenuState] = useState<ContextMenuState>(initialState);

  const showContextMenu = useCallback((
    position: Position,
    items: ContextMenuItem[],
    targetType: 'desktop' | 'icon' | 'taskbar' = 'desktop',
    targetId?: string
  ) => {
    setMenuState({
      isOpen: true,
      position,
      items,
      targetType,
      targetId,
    });
  }, []);

  const hideContextMenu = useCallback(() => {
    setMenuState(initialState);
  }, []);

  return (
    <ContextMenuContext.Provider
      value={{
        menuState,
        showContextMenu,
        hideContextMenu,
      }}
    >
      {children}
    </ContextMenuContext.Provider>
  );
};

export const useContextMenu = (): ContextMenuContextType => {
  const context = useContext(ContextMenuContext);
  if (!context) {
    throw new Error('useContextMenu must be used within a ContextMenuProvider');
  }
  return context;
};
